import { HTTPClient } from '@abaco/web-common/src/HTTP';
// import type { AxiosResponse } from "axios";
import type {
    CertificateCodesDictionary,
    DamageCodesDictionary,
    DamageClaimEvent,
    PcertItem,
    DamageClaimRecordRequest,
    DamageClaimProduct,
    DamageClaimPhotos,
    CodeMap,
    DamageClaimRecord,
    CertigrafPlotSelection,
    CertigrafPlot,
} from "../models/DamageClaimModel";
import type {
    CertigrafChecks,
    CertigrafPrecertificateData,
    CertigrafOrganizationsDictionary,
    SignUserData,
    StartSignResponse,
} from "../models/CertigrafModel";

export default class DamageClaimApi {
    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public getHttp(): HTTPClient {
        return this.http;
    }

    /**
     * Restituisce la lista dei codici di decodifica relativi ai precertificati
     */
    public async getCertificateCodes() {
        return this.http.get("/pcert-api/api/v1/code/codes")
            .then(({ data }: { data: Record<keyof CertificateCodesDictionary, CodeMap[]> }) => {
                const records = {} as Record<keyof CertificateCodesDictionary, Record<CodeMap['s_code'], CodeMap>>;
                const keys = Object.keys(data) as Array<keyof CertificateCodesDictionary>;
                for(let k = keys.length; k--; ){
                    records[keys[k]] = {};
                    for(let i = data[keys[k]].length; i--; )
                        records[keys[k]][data[keys[k]][i].s_code] = data[keys[k]][i];
                }
                return records;
            });
    }

    /**
     * Restituisce la lista dei codici di decodifica relativi ai damage claim
     */
    public async getDamageClaimCodes() {
        return this.http.get("/damage-claim-api/api/v1/code/codes")
            .then(({ data }: { data: Record<keyof DamageCodesDictionary, CodeMap[]> }) => {
                const records = {} as Record<keyof DamageCodesDictionary, Record<CodeMap['s_code'], CodeMap>>;
                const keys = Object.keys(data) as Array<keyof DamageCodesDictionary>;
                for(let k = keys.length; k--; ){
                    records[keys[k]] = {};
                    for(let i = data[keys[k]].length; i--; )
                        records[keys[k]][data[keys[k]][i].s_code] = data[keys[k]][i];
                }
                return records;
            });
    }

    /**
     *
     */
    public async getAvailableOrganizations(): Promise<CertigrafOrganizationsDictionary | null> {
        return (await this.http.get("/pcert-api/api/v1/org/orgs")).data;
    }

    /**
     * Restituisce tutti i damage claim con i dati farmId (subjectId) e campaign
     * @param subjectId
     * @param campaignYear
     */
    public async getDamageClaimList(subjectId: number, campaignYear: number/*, filterObject: DamageClaimFilter = {}*/) {
        return this.http.get("/damage-claim-api/api/v1/dclaim/damage-claims?campaign=" + campaignYear + "&farmId=" + subjectId)
            .then(({ data = [] }: { data: DamageClaimRecord[] }) => {
                const records: Record<number, DamageClaimRecord> = {};
                for (let i = data.length; i--;)
                    records[data[i].damage_id] = data[i];
                return records;
            });
    }

    /**
     *
     * @param campaignYear
     */
    public async getDamageClaimListIntermediary(campaignYear: number/*, filterObject: DamageClaimFilter = {}*/) {
        return this.http.get("/damage-claim-api/api/v1/dclaim/damage-claims-intermediary?campaign=" + campaignYear)
            .then(({ data = [] }: { data: DamageClaimRecord[] }) => {
                const records: Record<number, DamageClaimRecord> = {};
                for (let i = data.length; i--;)
                    records[data[i].damage_id] = data[i];
                return records;
            });
    }

    /**
     *
     * @param campaignYear
     */
    public async getDamageClaimListCondifesa(campaignYear: number/*, filterObject: DamageClaimFilter = {}*/) {
        return this.http.get("/damage-claim-api/api/v1/dclaim/damage-claims-condifesa?campaign=" + campaignYear)
            .then(({ data = [] }: { data: DamageClaimRecord[] }) => {
                const records: Record<number, DamageClaimRecord> = {};
                for (let i = data.length; i--;)
                    records[data[i].damage_id] = data[i];
                return records;
            });
    }

    /**
     * Restituisce un damage claim dato il dclaimId (damage_id)
     * @param dclaimId
     */
    public async getDamageClaimData(dclaimId: number): Promise<DamageClaimEvent> {
        return (await this.http.get("/damage-claim-api/api/v1/dclaim/damage-claims/" + dclaimId)).data
    }

    /**
     * Aggiunge o modifica un damage claim
     * (damage_id necessario in caso di modifica)
     * @param body
     */
    public async setDamageClaimData(body: DamageClaimRecordRequest): Promise<DamageClaimRecord> {
        return (await this.http.post("/damage-claim-api/api/v1/dclaim/", body)).data
    }

    /**
     * Elimina un damage claim dato il dclaimId (damage_id) in tutte le tabelle relative al damage claim
     * @param dclaimId
     */
    public async deleteDamageClaimData(dclaimId: number) {
        return this.http.delete("/damage-claim-api/api/v1/dclaim/" + dclaimId)
    }

    /**
     * Restituisce la lista di pre-certificati di un business (farmId) [Wizard Step 1]
     * @param subjectId
     * @param campaignYear
     */
    public async getPrecertificateList(subjectId: number, campaignYear: number/*, filterObject: DamageClaimFilter = {}*/) {
        return this.http.get("/damage-claim-api/api/v1/farm/" + subjectId + "/pcerts?campaign=" + campaignYear)
            .then(({ data = [] }: { data: PcertItem[] }) => {
                const records: Record<number, PcertItem> = {};
                for (let i = data.length; i--;)
                    records[data[i].pcert_id] = data[i];
                return records;
            });
    }

    // /**
    //  *
    //  * @param farm_id
    //  * @param pcert_id
    //  */
    // public async getPrecertificate(farm_id: number, pcert_id: number): Promise<PcertItem> {
    //     return (await this.http.get("/api/v1/farm/" + farm_id + "/pcert/" + pcert_id)).data
    // }
    // /**
    //  *
    //  * @param campaign
    //  */
    // public async getPrecertificateListIntermediary(campaign: number) {
    //     return (await this.http.get("/pcert-api/api/v1/intermediary/pcert/list?campaign=" + campaign)).data
    // }
    //
    // /**
    //  *
    //  * @param campaign
    //  */
    // public async getPrecertificateListCondifesa(campaign: number) {
    //     return (await this.http.get("/pcert-api/api/v1/condifesa/pcert/list?campaign=" + campaign)).data
    // }


    /**
     * Restituisce la lista dei plot selezionati in un pre-certificato relativo ad un damage claim [Wizard Step 2].
     * NB: il valore fl_valid indica la selezione del plot nel damage claim, non nel pre certificato.
     * @param dclaimId
     */
    public async getPlotList(dclaimId: number) {
        return this.http.get("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/plots")
            .then(({ data = [] }: { data: CertigrafPlot[] }) => {
                const records: Record<number, CertigrafPlot> = {};
                for (let i = data.length; i--;)
                    records[data[i].pkid] = data[i];
                return records;
            });
    }

    /**
     * Seleziona o deseleziona i plot di un damage claim utilizzando il flag fl_valid [Wizard Step 2]
     * @param dclaimId
     * @param payload
     */
    public async setDamageClaimPlots(dclaimId: number, payload: CertigrafPlotSelection[] = []) {
        return this.http.post("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/plots", payload)
    }

    /**
     *
     * @param subjectId
     * @param pcertId
     * @param campaignYear
     */
    public async getOtherPlots(subjectId: number, pcertId: number, campaignYear: number): Promise<CertigrafPlot[]> {
        return (await this.http.get("/pcert-api/api/v1/farm/" + subjectId + "/pcert/" + pcertId + "/other_plots?campaign=" + campaignYear)).data
    }

    // /**
    //  *
    //  * @param subjectId
    //  * @param pcertId
    //  * @param plotPkid
    //  */
    // public async checkPlot(subjectId: number, pcertId: number, plotPkid: number): Promise<CertigrafChecks | null> {
    //     try {
    //         await this.http.post("/pcert-api/api/v1/farm/" + subjectId + "/pcert/" + pcertId + "/plots/" + plotPkid + "/checks");
    //         return null;
    //     } catch(e) {
    //         if(e.response?.status === 400) {
    //             return e.response.data;
    //         }
    //         throw e;
    //     }
    // }


    /**
     * Restituisce il prodotto del damage claim indicato
     * @param dclaimId
     */
    public async getDamageClaimProduct(dclaimId: number): Promise<DamageClaimProduct> {
        return (await this.http.get("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/product")).data;
    }

    /**
     *
     * @param subjectId
     * @param pcertId
     */
    public async totalPlotAreas(subjectId: number, pcertId: number): Promise<{ lot_area_tot: number; ensure_area_tot: number }> {
        return (await this.http.get("/pcert-api/api/v1/farm/" + subjectId + "/pcert/" + pcertId + "/plots/areaTot")).data
    }

    /**
     * Aggiunge o modifica un prodotto al damage claim indicato
     * @param dclaimId
     * @param body
     */
    public async setDamageClaimProduct(dclaimId: number, body: Omit<DamageClaimProduct, "damage_id">) {
        return this.http.post("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/product", body)
    }

    /**
     * Restituisce il evento del damage claim indicato
     * @param dclaimId
     */
    public async getDamageClaimEvent(dclaimId: number): Promise<DamageClaimEvent> {
        return (await this.http.get("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/event")).data;
    }

    /**
     * Aggiunge o modifica un evento al damage claim indicato
     * @param dclaimId
     * @param body
     */
    public async setDamageClaimEvent(dclaimId: number, body: Omit<DamageClaimEvent, "damage_id">) {
        return this.http.post("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/event", body)
    }

    /**
     * Restituisce la foto del damage claim indicato
     * @param dclaimId
     */
    public async getDamageClaimPhoto(dclaimId: number): Promise<DamageClaimPhotos> {
        return (await this.http.get("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/photo?fileContent=true")).data;
    }

    /**
     * Aggiunge o modifica una foto al damage claim indicato
     * @param dclaimId
     * @param photoFile
     */
    public async setDamageClaimPhoto(dclaimId: number, photoFile: File) {
        const formData = new FormData();
        formData.append("photoFile", photoFile);
        return this.http.post("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/upload-photo", formData,{
            // responseType: "blob",
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    }

    /**
     *
     * @param subjectId
     * @param dclaimId
     */
    public async getCertificateDetails(subjectId: number, dclaimId: number): Promise<CertigrafPrecertificateData> {
        return (await this.http.get("/pcert-api/api/v1/farm/" + subjectId + "/pcert/" + dclaimId)).data
    }

    /**
     *
     * @param dclaimId
     */
    public async getSignedPrint(dclaimId: number): Promise<Blob>
    {
        return (await this.http.get("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/print", {responseType: "blob"})).data
    }

    /**
     *
     * @param dclaimId
     * @param userData
     * @param pdfFileName
     * @param pdfBase64
     * @param signBoundary
     * @param page
     */
    public async startSignProcedure(
        dclaimId: number,
        userData: SignUserData,
        pdfFileName: string,
        pdfBase64: string,
        signBoundary: [number, number, number, number],
        page: number
    ): Promise<StartSignResponse | CertigrafChecks>
    {
        try {
            return (await this.http.post("/damage-claim-api/api/v1/dclaim/" + dclaimId + "/sign/start", {
                first_name: userData.name,
                last_name: userData.surname,
                email: userData.email,
                phone: userData.telephone,
                file_name: pdfFileName,
                page: page,
                position: signBoundary,
                file_content: pdfBase64
            })).data;
        } catch(e) {
            if(e.response?.status === 400) {
                return e.response.data;
            }
            throw e;
        }
    }
}
